'use client';

import React, { useContext } from 'react';
import NextLink from 'next/link';
import { usePathname } from 'next/navigation';
import classNames from 'classnames';
import { MenuItem } from '@headlessui/react';
import { Text } from '../Text/Text';
import Image from '../Image/Image';
import Icon from './Icon';
import Flag from '../../static/Flag';
import { MenuContext } from '../Dropdown/MenuContext';
import { getLinkStyle, getLinkClasses } from './LinkHelpers';
import { getFileIdFromUrl } from '../../../api/api';

export const transparentLinkProps = {
	background: 'transparent',
	background2: '',
	color: 'color',
	text: 'Link',
	borderWidth: 0,
};

export { getLinkStyle, getLinkClasses };

// Helper function to handle file downloads
const createFileClickHandler = (fileUrl) => (e) => {
	if (fileUrl) {
		e.preventDefault();
		const fileId = getFileIdFromUrl(fileUrl);
		if (fileId) {
			const link = document.createElement('a');
			link.href = fileUrl;
			link.download = '';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		}
	}
};

// Helper function to determine final href
const getFinalHref = (href, fileUrl) => {
	if (fileUrl) return '#';
	if (href.includes('/') || href.includes('mailto:') || href.startsWith('#')) return href;
	return `/${href}`;
};

// Helper function to check if link is external
const isExternalLink = (href) => href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('http://') || href.startsWith('https://');

// Component to render link content
function LinkContent({ iconBefore, image, imageComponent, height, width, src, textComponent, text, fontFamily, settings }) {
	return (
		<div className="inline-flex items-center align-middle text-center gap-x-2">
			{iconBefore && (iconBefore.length === 2 ? <Flag code={iconBefore} /> : <Icon value={iconBefore} />)}
			{image ? (
				<Image {...imageComponent} height={height} width={width} src={src} className="h-12" />
			) : (
				<Text {...textComponent} color="" text={text} fontFamily={fontFamily} textAlign="left" settings={settings} />
			)}
		</div>
	);
}

// Wrapper component to conditionally render <a> or <NextLink>
function LinkWrapper({ href, isExternal, style, className, targetBlank, onClick, children }) {
	const commonProps = {
		style,
		className,
		target: targetBlank ? '_blank' : undefined,
		href,
		onClick,
	};

	return isExternal ? <a {...commonProps}>{children}</a> : <NextLink {...commonProps}>{children}</NextLink>;
}

function MenuItemLink({ href, style, className, targetBlank, iconBefore, image, imageComponent, height, width, src, textComponent, text, fileUrl, fontFamily, settings }) {
	const finalHref = getFinalHref(href, fileUrl);

	return (
		<MenuItem>
			<LinkWrapper
				href={finalHref}
				isExternal={isExternalLink(finalHref)}
				style={style}
				className={className}
				targetBlank={targetBlank}
				onClick={createFileClickHandler(fileUrl)}
			>
				<LinkContent
					iconBefore={iconBefore}
					image={image}
					imageComponent={imageComponent}
					height={height}
					width={width}
					src={src}
					textComponent={textComponent}
					text={text}
					fontFamily={fontFamily}
					settings={settings}
				/>
			</LinkWrapper>
		</MenuItem>
	);
}

export function Link(props) {
	const { href, settings, text, textComponent, image, height, width, imageComponent, targetBlank, src, iconBefore, textCenter, fileUrl, fontFamily, highlightActive } = props;
	const isInMenu = useContext(MenuContext);
	const pathname = usePathname();

	const finalHrefForActive = getFinalHref(href || '', fileUrl);
	const isActive = highlightActive && !!pathname && finalHrefForActive === pathname;

	if (isInMenu) {
		return (
			<MenuItemLink
				href={href}
				style={getLinkStyle(props, settings, isActive)}
				className={classNames(
					getLinkClasses(props, settings),
					textCenter ? 'block' : 'inline-flex',
					'data-[focus]:opacity-75',
				)}
				targetBlank={targetBlank}
				iconBefore={iconBefore}
				image={image}
				imageComponent={imageComponent}
				height={height}
				width={width}
				src={src}
				textComponent={textComponent}
				text={text}
				fileUrl={fileUrl}
				fontFamily={fontFamily}
				settings={settings}
			/>
		);
	}

	const finalHref = getFinalHref(href, fileUrl);

	return (
		<LinkWrapper
			href={finalHref}
			isExternal={isExternalLink(finalHref)}
			style={getLinkStyle(props, settings, isActive)}
			className={classNames(
				getLinkClasses(props, settings),
				textCenter ? 'block' : 'inline-flex',
				'hover:opacity-75',
			)}
			targetBlank={targetBlank}
			onClick={createFileClickHandler(fileUrl)}
		>
			<LinkContent
				iconBefore={iconBefore}
				image={image}
				imageComponent={imageComponent}
				height={height}
				width={width}
				src={src}
				textComponent={textComponent}
				text={text}
				fontFamily={fontFamily}
				settings={settings}
			/>
		</LinkWrapper>
	);
}
